import React, { useState } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Divider,
  Button,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { MuiTelInput } from 'mui-tel-input';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Modal from 'components/common/customModal';
import userProfileStore from 'store/userProfileStore';
import domainData from 'store/domainStore';
import userActivateOptions from 'constants/user-activate-options.js';

import { useModal } from 'hooks/common';
import { getTimezoneLists } from 'helpers/helper';
import { isSuperAdmin } from 'helpers/usersRolesHelper';
import M from 'messages';
import ChangePasswordModal from '../changePasswordModal';
import ChangeEmailModal from '../changeEmailModal';
import classes from './styles';

const AccountDetailsForm = observer(({ submitChangePassword, isProfile, initialProfileData, getProfile }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenEmailChangeModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseEmailChangeModal = async (_event, _action, emailUpdated) => {
    if (emailUpdated) {
      await getProfile();
    }
    userProfileStore.clearEmailChangeData();
    setIsModalOpen(false);
  };

  const currentUser = domainData.getCurrentUser();
  const isUserSuperAdmin = isSuperAdmin(currentUser);
  const [isOpenModal, openModal, closeModal] = useModal(false);

  const data = userProfileStore.getUserProfileData();

  const handleCloseModal = () => {
    userProfileStore.clearChangePasswordDataAndErrors();
    closeModal();
  };

  const handleChangePassword = () => {
    const changePasswordData = userProfileStore.getChangePasswordData();
    submitChangePassword(changePasswordData);
    handleCloseModal();
  };

  const renderOption = (fieldProps, data) => {
    const city = data.city.split('/').pop().replaceAll('_', ' ');
    const { timezone } = data;

    return (
      <div style={classes.timezone} {...fieldProps} key={data.value + data.timezone}>
        <div>{city}</div>
        <div>{timezone}</div>
      </div>
    );
  };

  return (
    <>
      {isProfile && (
        <>
          <ChangeEmailModal open={isModalOpen} handleClose={handleCloseEmailChangeModal} />
          <Modal
            open={isOpenModal}
            handleSubmit={handleChangePassword}
            handleClose={handleCloseModal}
            disableSave={!userProfileStore.getAllowModalSubmit() || !userProfileStore.isChangePasswordBtnActive()}
            title={M.get('account.changePassword')}
            leftBtnText={M.get('actions.cancel')}
            rightBtnText={M.get('actions.save')}
            actionFooterStyle="accountActionColumn"
            dialogContentClassName="accountModalContent"
            modalClassName="accountModals"
            submitBtnVariant="contained"
            footerBtnStyle="accountBtnStyle"
            dialogHeader="changePasswordModalTitle">
            <ChangePasswordModal initialProfileData={initialProfileData} />
          </Modal>
        </>
      )}
      <Grid container sx={classes.detailsContainer}>
        <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={classes.fields}>
          <Grid size={{ xs: 12, sm: 5 }}>
            <Typography sx={classes.fieldName}>{M.get('account.name')}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 7 }}>
            <TextField
              type="text"
              id="name"
              name="name"
              variant="standard"
              sx={classes.fieldValue}
              placeholder={M.get('account.name')}
              required
              value={data.name}
              onChange={userProfileStore.onChangeField}
              InputProps={{
                inputProps: { maxLength: 255 },
              }}
            />
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }} sx={classes.fields}>
          <Grid size={{ xs: 5 }}>
            <Typography sx={classes.fieldName}>{M.get('account.email')}</Typography>
          </Grid>
          <Grid size={{ xs: 7 }}>
            <Grid container spacing={1} sx={classes.emailField}>
              <Grid size={{ xs: 10 }}>
                <Typography sx={classes.fieldValue}>{data.email}</Typography>
              </Grid>
              {isProfile && (
                <Grid size={{ xs: 2 }} sx={classes.changeIconContainer}>
                  <Box sx={classes.InputAdornment}>
                    <EditIcon onClick={handleOpenEmailChangeModal} />
                  </Box>
                </Grid>
              )}
            </Grid>
            <Divider />
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }} sx={classes.fields}>
          <Grid size={{ xs: 5 }}>
            <Typography sx={{ ...classes.fieldName, ...classes.fieldNameWithTooltip }}>
              {M.get('account.phone')}
              <Tooltip placement="top" title={M.get('account.phoneTooltip')}>
                <HelpOutlineIcon sx={classes.fieldHeaderIcon} />
              </Tooltip>
            </Typography>
          </Grid>
          <Grid size={{ xs: 7 }}>
            <MuiTelInput
              id="phone"
              name="phone"
              sx={classes.fieldValue}
              value={data.phone}
              variant="standard"
              required
              defaultCountry="US"
              onChange={userProfileStore.onChangePhoneField}
            />
            <Divider />
          </Grid>
        </Grid>
        {isProfile && (
          <Grid size={{ xs: 12 }} sx={classes.fields}>
            <Grid size={{ xs: 5 }}>
              <Typography sx={classes.fieldName}>{M.get('account.password')}</Typography>
            </Grid>
            <Grid size={{ xs: 7 }}>
              <Button sx={classes.button} onClick={() => openModal()} color="primary" variant="text">
                {M.get('account.changePassword')}
              </Button>
              <Divider />
            </Grid>
          </Grid>
        )}
        <Grid size={{ xs: 12 }} sx={classes.fields}>
          <Grid size={{ xs: 5 }}>
            <Typography sx={classes.fieldName}>{M.get('account.role')}</Typography>
          </Grid>
          <Grid size={{ xs: 7 }}>
            <Typography sx={classes.fieldValue}>{M.get(`usersRoles.${data.usersRole.name}`)}</Typography>
            <Divider />
          </Grid>
        </Grid>
        {isProfile && (
          <Grid size={{ xs: 12 }} sx={classes.fields}>
            <Grid size={{ xs: 5 }}>
              <Typography sx={classes.fieldName}>{M.get('account.timezone')}</Typography>
            </Grid>
            <Grid size={{ xs: 7 }} sx={classes.rootSelectGrid}>
              <FormControl fullWidth>
                <Autocomplete
                  id="timezone"
                  value={getTimezoneLists.find(i => i.value === `${data.timezoneCity} ${data.timezone}`) || null}
                  renderOption={renderOption}
                  onChange={(_event, newValue) => userProfileStore.onChangeAutocomplete(newValue)}
                  options={getTimezoneLists}
                  getOptionLabel={option => option.label || ''}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  slotProps={{
                    listbox: {
                      style: { maxHeight: '235px' },
                    },
                  }}
                  classes={{ inputRoot: classes.rootAutoComplete }}
                  renderInput={params => <TextField {...params} variant="standard" />}
                />
              </FormControl>
              <Divider />
            </Grid>
          </Grid>
        )}
        {!isProfile && isUserSuperAdmin && (
          <>
            <Grid size={{ xs: 12 }} sx={classes.fields}>
              <Grid size={{ xs: 5 }}>
                <Typography sx={classes.fieldName}>{M.get('account.activityStatus')}</Typography>
              </Grid>
              <Grid size={{ xs: 7 }}>
                <FormControl fullWidth>
                  <Select
                    variant="standard"
                    value={data.isActive}
                    onChange={userProfileStore.onChangeField}
                    id="isActive-select"
                    name="isActive">
                    {userActivateOptions.map(opt => (
                      <MenuItem key={opt.id} value={opt.value}>
                        {opt.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={classes.fields}>
              <Grid size={{ xs: 12 }} sx={classes.fields}>
                <Grid size={{ xs: 5 }}>
                  <Typography sx={classes.fieldName}>{M.get('account.planException')}</Typography>
                </Grid>
                <Grid size={{ xs: 7 }}>
                  <FormControlLabel
                    variant="outlined"
                    labelPlacement="start"
                    control={
                      <Switch
                        id="planException"
                        color="primary"
                        name="planException"
                        onChange={userProfileStore.onChangeCheckBox}
                        checked={data.planException}
                      />
                    }
                    sx={classes.planException}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
});

AccountDetailsForm.propTypes = {
  submitChangePassword: PropTypes.func.isRequired,
  isProfile: PropTypes.bool,
  initialProfileData: PropTypes.shape({}).isRequired,
  getProfile: PropTypes.func.isRequired,
};

AccountDetailsForm.defaultValues = {
  isProfile: false,
};

export default AccountDetailsForm;
